import { isDarkSquare } from '../game/path.js';
import {
  BOARD_INLAY_OFFSET,
  BOARD_INLAY_SIZE,
  BOARD_OUTER_OFFSET,
  BOARD_OUTER_SIZE,
  CANVAS_SIZE,
  DEFAULT_BOARD_SIZE,
  SQUARE_SIZE,
  WOOD_FRAME_THICKNESS,
} from '../utils/constants.js';
import type { ViewerRotation } from './geometry.js';
import type { RenderPalette } from './palette.js';
import {
  getBorderWoodPattern as _getBorderWoodPattern,
  getFeltNoisePattern,
  getWoodPattern as _getWoodPattern,
} from '@bn-games/render-helpers';

const PATTERN_DIMS = { width: BOARD_INLAY_SIZE, height: BOARD_INLAY_SIZE };

const getWoodPattern = (ctx: CanvasRenderingContext2D, palette: RenderPalette) =>
  _getWoodPattern(ctx, palette, PATTERN_DIMS);
const getBorderWoodPattern = (ctx: CanvasRenderingContext2D, palette: RenderPalette) =>
  _getBorderWoodPattern(ctx, palette, PATTERN_DIMS);

const COLUMN_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'] as const;

export function drawBoard(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
  rotation: ViewerRotation = 0,
): void {
  drawCanvasBackground(ctx, palette);
  drawWoodFrame(ctx, palette);
  drawInlay(ctx, palette);
  drawCoordinateLabels(ctx, palette, rotation);
}

function drawCanvasBackground(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
): void {
  ctx.save();
  ctx.fillStyle = palette.borderWoodDark;
  ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
  ctx.restore();
}

function drawWoodFrame(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
): void {
  ctx.save();
  ctx.fillStyle = getBorderWoodPattern(ctx, palette);
  ctx.fillRect(BOARD_OUTER_OFFSET, BOARD_OUTER_OFFSET, BOARD_OUTER_SIZE, BOARD_OUTER_SIZE);

  // Outer bevel — light catches top/left, shadow on bottom/right.
  ctx.fillStyle = palette.innerHighlight;
  ctx.fillRect(BOARD_OUTER_OFFSET, BOARD_OUTER_OFFSET, BOARD_OUTER_SIZE, 2);
  ctx.fillRect(BOARD_OUTER_OFFSET, BOARD_OUTER_OFFSET, 2, BOARD_OUTER_SIZE);
  ctx.fillStyle = palette.innerShadow;
  ctx.fillRect(BOARD_OUTER_OFFSET, BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - 2, BOARD_OUTER_SIZE, 2);
  ctx.fillRect(BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - 2, BOARD_OUTER_OFFSET, 2, BOARD_OUTER_SIZE);

  // Recessed bevel around the playing inlay — a subtle dip into the wood.
  const ix = BOARD_INLAY_OFFSET;
  const iy = BOARD_INLAY_OFFSET;
  const iw = BOARD_INLAY_SIZE;
  const ih = BOARD_INLAY_SIZE;
  ctx.fillStyle = palette.innerShadow;
  ctx.fillRect(ix - 1, iy - 1, iw + 2, 1);
  ctx.fillRect(ix - 1, iy - 1, 1, ih + 2);
  ctx.fillStyle = palette.innerHighlight;
  ctx.fillRect(ix - 1, iy + ih, iw + 2, 1);
  ctx.fillRect(ix + iw, iy - 1, 1, ih + 2);

  // Brass diamond ornaments at the wood frame corners.
  const cornerInset = WOOD_FRAME_THICKNESS / 2;
  const corners: { x: number; y: number }[] = [
    { x: BOARD_OUTER_OFFSET + cornerInset, y: BOARD_OUTER_OFFSET + cornerInset },
    {
      x: BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - cornerInset,
      y: BOARD_OUTER_OFFSET + cornerInset,
    },
    {
      x: BOARD_OUTER_OFFSET + cornerInset,
      y: BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - cornerInset,
    },
    {
      x: BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - cornerInset,
      y: BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - cornerInset,
    },
  ];
  for (const c of corners) drawBrassDiamond(ctx, c.x, c.y);

  ctx.restore();
}

function drawInlay(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
): void {
  ctx.save();
  ctx.fillStyle = getWoodPattern(ctx, palette);
  ctx.fillRect(BOARD_INLAY_OFFSET, BOARD_INLAY_OFFSET, BOARD_INLAY_SIZE, BOARD_INLAY_SIZE);
  ctx.restore();

  for (let row = 0; row < DEFAULT_BOARD_SIZE; row += 1) {
    for (let col = 0; col < DEFAULT_BOARD_SIZE; col += 1) {
      drawSquare(ctx, row, col, palette);
    }
  }

  // Vignette across the whole playing surface for depth.
  ctx.save();
  const cx = BOARD_INLAY_OFFSET + BOARD_INLAY_SIZE / 2;
  const cy = BOARD_INLAY_OFFSET + BOARD_INLAY_SIZE / 2;
  const grad = ctx.createRadialGradient(
    cx,
    cy,
    BOARD_INLAY_SIZE * 0.25,
    cx,
    cy,
    BOARD_INLAY_SIZE * 0.65,
  );
  grad.addColorStop(0, 'transparent');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0.18)');
  ctx.fillStyle = grad;
  ctx.fillRect(BOARD_INLAY_OFFSET, BOARD_INLAY_OFFSET, BOARD_INLAY_SIZE, BOARD_INLAY_SIZE);
  ctx.restore();
}

function drawSquare(
  ctx: CanvasRenderingContext2D,
  row: number,
  col: number,
  palette: RenderPalette,
): void {
  const x = BOARD_INLAY_OFFSET + col * SQUARE_SIZE;
  const y = BOARD_INLAY_OFFSET + row * SQUARE_SIZE;
  const dark = isDarkSquare({ row, col });
  const base = dark ? palette.darkSquareBase : palette.lightSquareBase;
  const deep = dark ? palette.darkSquareDeep : palette.lightSquareDeep;

  ctx.save();
  const grad = ctx.createLinearGradient(x, y, x, y + SQUARE_SIZE);
  grad.addColorStop(0, base);
  grad.addColorStop(1, deep);
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, SQUARE_SIZE, SQUARE_SIZE);

  // Felt-fiber sheen — heavier on dark squares so the playable squares feel
  // worn while the light squares stay clean.
  ctx.globalAlpha = dark ? 0.22 : 0.1;
  ctx.fillStyle = getFeltNoisePattern(ctx);
  ctx.fillRect(x, y, SQUARE_SIZE, SQUARE_SIZE);
  ctx.globalAlpha = 1;

  // Square boundary hairline — barely visible, just enough to separate.
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
  ctx.lineWidth = 0.5;
  ctx.strokeRect(x + 0.25, y + 0.25, SQUARE_SIZE - 0.5, SQUARE_SIZE - 0.5);
  ctx.restore();
}

function drawCoordinateLabels(
  ctx: CanvasRenderingContext2D,
  palette: RenderPalette,
  rotation: ViewerRotation,
): void {
  ctx.save();
  ctx.fillStyle = palette.labelColor;
  ctx.font = '600 12px "Segoe UI", system-ui, sans-serif';
  ctx.textBaseline = 'middle';

  const labelGap = WOOD_FRAME_THICKNESS / 2;

  // Column labels (a–h) along top and bottom of the wood frame.
  ctx.textAlign = 'center';
  for (let i = 0; i < DEFAULT_BOARD_SIZE; i += 1) {
    const col = rotation === 0 ? i : DEFAULT_BOARD_SIZE - 1 - i;
    const label = COLUMN_LABELS[col] ?? '';
    const centerX = BOARD_INLAY_OFFSET + i * SQUARE_SIZE + SQUARE_SIZE / 2;
    ctx.fillText(label, centerX, BOARD_OUTER_OFFSET + labelGap);
    ctx.fillText(label, centerX, BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - labelGap);
  }

  // Row labels (8–1 top→bottom for white perspective) along left and right.
  ctx.textAlign = 'center';
  for (let i = 0; i < DEFAULT_BOARD_SIZE; i += 1) {
    const row = rotation === 0 ? i : DEFAULT_BOARD_SIZE - 1 - i;
    const rank = DEFAULT_BOARD_SIZE - row;
    const centerY = BOARD_INLAY_OFFSET + i * SQUARE_SIZE + SQUARE_SIZE / 2;
    ctx.fillText(String(rank), BOARD_OUTER_OFFSET + labelGap, centerY);
    ctx.fillText(
      String(rank),
      BOARD_OUTER_OFFSET + BOARD_OUTER_SIZE - labelGap,
      centerY,
    );
  }

  ctx.restore();
}

function drawBrassDiamond(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
): void {
  const r = 5;
  ctx.save();

  ctx.beginPath();
  ctx.moveTo(cx, cy - r - 1);
  ctx.lineTo(cx + r + 1, cy);
  ctx.lineTo(cx, cy + r + 1);
  ctx.lineTo(cx - r - 1, cy);
  ctx.closePath();
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.fill();

  const grad = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
  grad.addColorStop(0, '#ffe8a4');
  grad.addColorStop(0.5, '#d4a849');
  grad.addColorStop(1, '#7a5a1f');
  ctx.beginPath();
  ctx.moveTo(cx, cy - r);
  ctx.lineTo(cx + r, cy);
  ctx.lineTo(cx, cy + r);
  ctx.lineTo(cx - r, cy);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  ctx.lineWidth = 0.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(cx, cy, 0.8, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(60, 35, 10, 0.9)';
  ctx.fill();

  ctx.restore();
}
